import axiosInstance from '../utils/axios';

class AccountingService {
  // Chart of Accounts
  async getChartOfAccounts(schoolID, filters = {}) {
    try {
      const response = await axiosInstance.get(`/api/accounting/chart-of-accounts/${schoolID}`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching chart of accounts:', error);
      if (error.response?.status === 404) {
        return []; // Return empty array instead of throwing
      }
      if (error.response?.status === 503) {
        throw new Error('Database connection unavailable. Please try again later.');
      }
      throw error;
    }
  }

  async createAccount(accountData) {
    try {
      const response = await axiosInstance.post('/api/accounting/chart-of-accounts', accountData);
      return response.data;
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  }

  async updateAccount(accountId, accountData) {
    try {
      const response = await axiosInstance.put(`/api/accounting/chart-of-accounts/${accountId}`, accountData);
      return response.data;
    } catch (error) {
      console.error('Error updating account:', error);
      throw error;
    }
  }

  async deleteAccount(accountId) {
    try {
      const response = await axiosInstance.delete(`/api/accounting/chart-of-accounts/${accountId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  }

  // Journal Entries
  async getJournalEntries(schoolID, filters = {}) {
    try {
      const params = new URLSearchParams({ schoolID, ...filters });
      const response = await axiosInstance.get(`/api/accounting/journal-entries/${schoolID}?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      throw error;
    }
  }

  async createJournalEntry(entryData) {
    try {
      const response = await axiosInstance.post('/api/accounting/journal-entries', entryData);
      return response.data;
    } catch (error) {
      console.error('Error creating journal entry:', error);
      throw error;
    }
  }

  async updateJournalEntry(entryId, entryData) {
    try {
      const response = await axiosInstance.put(`/api/accounting/journal-entries/${entryId}`, entryData);
      return response.data;
    } catch (error) {
      console.error('Error updating journal entry:', error);
      throw error;
    }
  }

  async postJournalEntry(entryId, userId, userName) {
    try {
      const response = await axiosInstance.post(`/api/accounting/journal-entries/${entryId}/post`, {
        userId,
        userName
      });
      return response.data;
    } catch (error) {
      console.error('Error posting journal entry:', error);
      throw error;
    }
  }

  // Financial Periods
  async getFinancialPeriods(schoolID) {
    try {
      const response = await axiosInstance.get(`/api/accounting/financial-periods/${schoolID}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching financial periods:', error);
      throw error;
    }
  }

  async createFinancialPeriod(periodData) {
    try {
      const response = await axiosInstance.post('/api/accounting/financial-periods', periodData);
      return response.data;
    } catch (error) {
      console.error('Error creating financial period:', error);
      throw error;
    }
  }

  // Financial Reports
  async getTrialBalance(schoolID, startDate, endDate) {
    try {
      const response = await axiosInstance.post('/api/accounting/trial-balance', {
        schoolID,
        startDate,
        endDate
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching trial balance:', error);
      throw error;
    }
  }

  async getBalanceSheet(schoolID, asOfDate) {
    try {
      const response = await axiosInstance.post('/api/accounting/balance-sheet', {
        schoolID,
        asOfDate
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching balance sheet:', error);
      throw error;
    }
  }

  async getIncomeStatement(schoolID, startDate, endDate) {
    try {
      const response = await axiosInstance.post('/api/accounting/income-statement', {
        schoolID,
        startDate,
        endDate
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching income statement:', error);
      throw error;
    }
  }

  async getCashFlowStatement(schoolID, startDate, endDate) {
    try {
      const response = await axiosInstance.post('/api/accounting/cash-flow', {
        schoolID,
        startDate,
        endDate
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching cash flow statement:', error);
      throw error;
    }
  }

  async getGeneralLedger(schoolID, startDate, endDate, accountCode = null) {
    try {
      const response = await axiosInstance.post('/api/accounting/general-ledger', {
        schoolID,
        startDate,
        endDate,
        accountCode
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching general ledger:', error);
      throw error;
    }
  }

  async getNominalLedger(schoolID, startDate, endDate) {
    try {
      const response = await axiosInstance.post('/api/accounting/nominal-ledger', {
        schoolID,
        startDate,
        endDate
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching nominal ledger:', error);
      throw error;
    }
  }

  // Overdue & Defaulters
  async getOverdueFees(schoolID, filters = {}) {
    try {
      const response = await axiosInstance.post('/api/accounting/overdue-fees', {
        schoolID,
        ...filters
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching overdue fees:', error);
      throw error;
    }
  }

  async getDefaultersList(schoolID, filters = {}) {
    try {
      const response = await axiosInstance.post('/api/accounting/defaulters', {
        schoolID,
        ...filters
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching defaulters list:', error);
      throw error;
    }
  }

  async getPaymentHistory(schoolID, filters = {}) {
    try {
      const response = await axiosInstance.post('/api/accounting/payment-history', {
        schoolID,
        ...filters
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  }

  // Dashboard & Analytics
  async getDashboardData(schoolID, period = 'current') {
    try {
      const response = await axiosInstance.post('/api/accounting/dashboard', {
        schoolID,
        period
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  async getFinancialForecast(schoolID, months = 12) {
    try {
      const response = await axiosInstance.post('/api/accounting/forecast', {
        schoolID,
        months
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching financial forecast:', error);
      throw error;
    }
  }

  // Audit & Compliance
  async getAuditLogs(schoolID, filters = {}) {
    try {
      const params = new URLSearchParams({ schoolID, ...filters });
      const response = await axiosInstance.get(`/api/accounting/audit-logs/${schoolID}?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  }

  async getGESComplianceReport(schoolID, period) {
    try {
      const response = await axiosInstance.post('/api/accounting/ges-compliance', {
        schoolID,
        period
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching GES compliance report:', error);
      throw error;
    }
  }

  // Export & Print
  async exportReport(reportType, data, format = 'pdf') {
    try {
      const response = await axiosInstance.post(`/api/accounting/export/${reportType}`, {
        data,
        format
      }, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting report:', error);
      throw error;
    }
  }

  async printReport(reportType, data) {
    try {
      const response = await axiosInstance.post(`/api/accounting/print/${reportType}`, {
        data
      });
      return response.data;
    } catch (error) {
      console.error('Error printing report:', error);
      throw error;
    }
  }

  // Utility Methods
  formatCurrency(amount, currency = null, locale = null) {
    // Get from localStorage if not provided
    const savedCountry = localStorage.getItem('schoolCountry');
    let finalCurrency = (currency ? String(currency).toUpperCase() : 'GHS') || 'GHS';
    let finalLocale = locale || 'en-GH';
    
    if (!currency && savedCountry) {
      try {
        const countryData = JSON.parse(savedCountry);
        // Derive from country if currency not set
        finalCurrency = (countryData.currency ? String(countryData.currency).toUpperCase() : null) || (countryData.country === 'LR' ? 'USD' : 'GHS');
        finalLocale = countryData.locale || (countryData.country === 'LR' ? 'en-US' : 'en-GH');
      } catch (e) {
        // Use defaults if parsing fails
      }
    }
    
    // Ensure currency is always a valid uppercase string
    finalCurrency = (finalCurrency ? String(finalCurrency).toUpperCase() : 'GHS') || 'GHS';
    
    return new Intl.NumberFormat(finalLocale || 'en-GH', {
      style: 'currency',
      currency: finalCurrency,
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  formatDate(date, locale = null) {
    const finalLocale = locale || (() => {
      const savedCountry = localStorage.getItem('schoolCountry');
      if (savedCountry) {
        try {
          const countryData = JSON.parse(savedCountry);
          return countryData.locale || 'en-GH';
        } catch (e) {
          return 'en-GH';
        }
      }
      return 'en-GH';
    })();
    
    return new Intl.DateTimeFormat(finalLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date(date));
  }

  calculateAccountBalance(account, journalEntries) {
    let debitTotal = 0;
    let creditTotal = 0;

    journalEntries.forEach(entry => {
      entry.entries.forEach(item => {
        if (item.accountCode === account.accountCode) {
          debitTotal += item.debit;
          creditTotal += item.credit;
        }
      });
    });

    const netBalance = debitTotal - creditTotal;

    if (account.normalBalance === 'Debit') {
      return {
        debit: Math.max(0, netBalance),
        credit: Math.max(0, -netBalance)
      };
    } else {
      return {
        debit: Math.max(0, -netBalance),
        credit: Math.max(0, netBalance)
      };
    }
  }

  validateJournalEntry(entries) {
    const debitTotal = entries.reduce((sum, entry) => sum + (entry.debit || 0), 0);
    const creditTotal = entries.reduce((sum, entry) => sum + (entry.credit || 0), 0);
    
    return {
      isBalanced: Math.abs(debitTotal - creditTotal) < 0.01,
      debitTotal,
      creditTotal,
      difference: Math.abs(debitTotal - creditTotal)
    };
  }
}

export default new AccountingService();
